import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import {
  useAllocateCli,
  useAllSharedCli,
  useAllocatedCli,
  useResellerUserPicker,
} from '../api';

/**
 * SADMIN / ADMIN flow — pick a user, pick one or more CLIs to grant them.
 *   - SADMIN sources from /api/obd/cli/all and allocates with allocated=1
 *   - ADMIN  sources from /api/obd/cli/allocated/:userId and allocates with allocated=2
 */
export function AllocateCliToUserPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const currentUserId = Number(useAuthStore((s) => s.userId));

  const isSAdmin =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;
  const allocatedFlag: 1 | 2 = isSAdmin ? 1 : 2;

  const allShared = useAllSharedCli();
  const adminAllocated = useAllocatedCli(isSAdmin ? null : currentUserId);
  const source = isSAdmin ? allShared : adminAllocated;

  const users = useResellerUserPicker();
  const allocate = useAllocateCli();

  const [targetUserId, setTargetUserId] = useState('');
  const [cliIds, setCliIds] = useState<string[]>([]);

  const cliOptions = useMemo(
    () =>
      (source.data ?? []).map((c) => ({
        value: String(c.cliId),
        label: c.pilotNumber ? `${c.cli} (${c.pilotNumber})` : c.cli,
      })),
    [source.data],
  );

  const userOptions = useMemo(
    () =>
      (users.data ?? []).map((u) => ({
        value: String(u.userId),
        label: u.name ? `${u.username} — ${u.name}` : u.username,
      })),
    [users.data],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUserId) {
      toast.error('Pick a user to allocate to.');
      return;
    }
    if (cliIds.length === 0) {
      toast.error('Pick at least one CLI.');
      return;
    }
    allocate.mutate(
      {
        userId: Number(targetUserId),
        cliList: cliIds.join(','),
        parent: currentUserId,
        allocated: allocatedFlag,
      },
      {
        onSuccess: (res) => {
          if (res?.message === 'Parameters Incorrect') {
            toast.error(res.message);
            return;
          }
          toast.success(res?.message ?? 'CLIs allocated');
          navigate('/cli-list');
        },
        onError: () => toast.error('Could not allocate CLIs.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Allocate CLI"
        description="Assign one or more CLIs to a user. They appear in the user's CLI list immediately."
        actions={
          <Button asChild variant="outline">
            <Link to="/cli-list">
              <ArrowLeft className="h-4 w-4" />
              Back to CLIs
            </Link>
          </Button>
        }
      />

      <Card className="sm:max-w-2xl">
        <CardHeader>
          <CardTitle>Allocation</CardTitle>
          <CardDescription>
            {isSAdmin
              ? 'Pick any CLI from the platform pool.'
              : 'Only CLIs allocated to you can be granted onward.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="targetUser">
                User<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select
                value={targetUserId}
                onValueChange={setTargetUserId}
                disabled={users.isLoading}
              >
                <SelectTrigger id="targetUser">
                  <SelectValue
                    placeholder={
                      users.isLoading ? 'Loading users…' : 'Pick a user'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label>
                CLIs<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <MultiSelect
                options={cliOptions}
                value={cliIds}
                onChange={setCliIds}
                placeholder={
                  source.isLoading ? 'Loading CLIs…' : 'Pick one or more CLIs'
                }
                searchPlaceholder="Filter CLIs…"
                disabled={source.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                {cliIds.length} selected.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={allocate.isPending}>
                {allocate.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Allocate
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/cli-list">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
